import {useId, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {
  IMPORTACION_MAX_BYTES,
  type ImportacionMatriz,
  reporteDeImportacionUrl,
  useImportarMatriz,
} from '@/hooks/sgsst/riesgos/use-ipevr'
import {downloadBlob} from '@/lib/api'
import {translateApiError} from '@/lib/errors'

// Pública y sin sesión (Firebase la sirve con no-cache): nunca lleva datos ni logo de un cliente.
const PLANTILLA_URL = '/plantillas/plantilla-importacion-ipevr-kairos-v1.xlsx'

const CONTEOS = {
  filas_leidas: 'ipevr.importar.resumen.filasLeidas',
  creadas: 'ipevr.importar.resumen.creadas',
  fusionadas: 'ipevr.importar.resumen.fusionadas',
  posibles_duplicados: 'ipevr.importar.resumen.posiblesDuplicados',
  con_error: 'ipevr.importar.resumen.conError',
  con_advertencias: 'ipevr.importar.resumen.conAdvertencias',
  propuestas: 'ipevr.importar.resumen.propuestas',
} as const satisfies Partial<Record<keyof ImportacionMatriz, string>>

function Resumen({resumen}: {resumen: ImportacionMatriz}) {
  const {t} = useTranslation()
  return (
    <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm" data-testid="ipevr-importar-resumen">
      {Object.entries(CONTEOS).map(([conteo, clave]) => (
        <div key={conteo} className="contents">
          <dt className="text-muted-foreground">{t(clave)}</dt>
          <dd className="text-right font-medium tabular-nums" data-testid={`ipevr-importar-${conteo}`}>
            {resumen[conteo as keyof typeof CONTEOS]}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function Progreso({pct}: {pct: number}) {
  const {t} = useTranslation()
  const fase = useId()
  return (
    <div className="space-y-1.5" data-testid="ipevr-importar-progreso">
      <p id={fase} className="text-sm">
        {t(pct < 100 ? 'ipevr.importar.subiendo' : 'ipevr.importar.importando')}
      </p>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-secondary"
        role="progressbar"
        aria-labelledby={fase}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}>
        <div className="h-full rounded-full bg-primary transition-all" style={{width: `${pct}%`}} />
      </div>
    </div>
  )
}

function Confirmar({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled: boolean
  onChange: (v: boolean) => void
}) {
  const {t} = useTranslation()
  const id = useId()
  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(marcada) => onChange(marcada === true)}
        data-testid="ipevr-importar-confirmar"
      />
      <label htmlFor={id} className="space-y-0.5 text-sm">
        <span className="block font-medium">{t('ipevr.importar.confirmar')}</span>
        <span className="block text-muted-foreground">{t('ipevr.importar.confirmarAyuda')}</span>
      </label>
    </div>
  )
}

export function ImportarMatrizDialog({
  businessId,
  puedeConfirmar,
  open,
  onOpenChange,
}: {
  businessId: string
  /** La API vuelve a exigir la licencia; aquí solo se ofrece la casilla a quien la tiene. */
  puedeConfirmar: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const importar = useImportarMatriz(businessId)
  const inputRef = useRef<HTMLInputElement>(null)
  const [progreso, setProgreso] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [resumen, setResumen] = useState<ImportacionMatriz | null>(null)
  const [descargando, setDescargando] = useState(false)
  const [confirmar, setConfirmar] = useState(false)
  const ocupado = progreso !== null || descargando

  function cambiarApertura(abierto: boolean) {
    if (ocupado) return
    if (!abierto) {
      setError(null)
      setResumen(null)
      setConfirmar(false)
    }
    onOpenChange(abierto)
  }

  async function cargar(files: FileList | null) {
    const file = files?.[0]
    if (inputRef.current) inputRef.current.value = ''
    if (!file) return
    setError(null)
    setResumen(null)
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setError(t('ipevr.importar.errors.tipo'))
      return
    }
    if (file.size > IMPORTACION_MAX_BYTES) {
      setError(t('ipevr.importar.errors.tamano'))
      return
    }
    setProgreso(0)
    try {
      setResumen(await importar.mutateAsync({file, confirmar: puedeConfirmar && confirmar, onProgress: setProgreso}))
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setProgreso(null)
    }
  }

  async function descargarReporte(importacionId: string) {
    setError(null)
    setDescargando(true)
    try {
      await downloadBlob(
        reporteDeImportacionUrl(businessId, importacionId),
        `reporte-importacion-${importacionId}.xlsx`
      )
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setDescargando(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={cambiarApertura}>
      <DialogContent className="max-w-lg" data-testid="ipevr-importar-dialog">
        <DialogHeader>
          <DialogTitle>{t('ipevr.importar.title')}</DialogTitle>
          <DialogDescription>{t('ipevr.importar.body')}</DialogDescription>
        </DialogHeader>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          hidden
          data-testid="ipevr-importar-input"
          onChange={(e) => cargar(e.target.files)}
        />
        {puedeConfirmar && !resumen && <Confirmar checked={confirmar} disabled={ocupado} onChange={setConfirmar} />}
        <div aria-live="polite" data-testid="ipevr-importar-estado">
          {progreso !== null && <Progreso pct={progreso} />}
          {resumen && <Resumen resumen={resumen} />}
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert" data-testid="ipevr-importar-error">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button type="button" size="sm" variant="outline" disabled={ocupado} onClick={() => cambiarApertura(false)}>
            {t('common.close')}
          </Button>
          {resumen ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={ocupado}
                data-testid="ipevr-importar-otro"
                onClick={() => inputRef.current?.click()}>
                {t('ipevr.importar.cargarOtro')}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={descargando}
                data-testid="ipevr-importar-reporte"
                onClick={() => descargarReporte(resumen.importacion_id)}>
                {t(descargando ? 'ipevr.importar.descargandoReporte' : 'ipevr.importar.descargarReporte')}
              </Button>
            </>
          ) : (
            <>
              <Button asChild size="sm" variant="outline">
                <a href={PLANTILLA_URL} download data-testid="ipevr-importar-plantilla">
                  {t('ipevr.importar.descargarPlantilla')}
                </a>
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={ocupado}
                data-testid="ipevr-importar-cargar"
                onClick={() => inputRef.current?.click()}>
                {t('ipevr.importar.cargar')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
